import React, { useState } from 'react'
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  TextField
} from '@mui/material'
import {
  Assessment as AssessmentIcon,
  Quiz as QuizIcon,
  CheckCircle as CheckIcon,
  School as SchoolIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { apiClient, API_ENDPOINTS } from '../config/api'
import toast from 'react-hot-toast'
import ReactMarkdown from 'react-markdown'

const assessmentTypes = [
  {
    type: 'IELTS Writing Task 1',
    title: 'IELTS Writing Task 1',
    description: 'Đánh giá kỹ năng viết mô tả biểu đồ, bảng, sơ đồ',
    icon: '📊',
    endpoint: API_ENDPOINTS.ASSESSMENT.WRITING_TASK1,
    streamEndpoint: API_ENDPOINTS.ASSESSMENT.WRITING_TASK1_STREAM
  },
  {
    type: 'IELTS Writing Task 2',
    title: 'IELTS Writing Task 2',
    description: 'Đánh giá kỹ năng viết luận theo tiêu chuẩn IELTS',
    icon: '✍️',
    endpoint: API_ENDPOINTS.ASSESSMENT.WRITING_TASK2,
    streamEndpoint: API_ENDPOINTS.ASSESSMENT.WRITING_TASK2_STREAM
  },
  {
    type: 'Speaking Assessment',
    title: 'Đánh giá Speaking',
    description: 'Đánh giá kỹ năng nói qua transcript',
    icon: '🗣️',
    endpoint: API_ENDPOINTS.ASSESSMENT.SPEAKING,
    streamEndpoint: API_ENDPOINTS.ASSESSMENT.SPEAKING_STREAM
  },
  {
    type: 'General Level',
    title: 'Đánh giá trình độ tổng quát',
    description: 'Đánh giá trình độ tiếng Anh tổng quát',
    icon: '🎯',
    endpoint: API_ENDPOINTS.TEACHER.ASSESS_LEVEL,
    streamEndpoint: API_ENDPOINTS.ASSESSMENT.GENERAL_STREAM
  }
]

const steps = ['Chọn loại đánh giá', 'Nhập nội dung', 'Xem kết quả']

function Assessment() {
  const [activeStep, setActiveStep] = useState(0)
  const [selectedAssessment, setSelectedAssessment] = useState(null)
  const [inputContent, setInputContent] = useState('')
  const [assessmentResult, setAssessmentResult] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [studentLevel, setStudentLevel] = useState('Intermediate')
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingAssessment, setStreamingAssessment] = useState('')

  const handleAssessmentSelect = (assessment) => {
    setSelectedAssessment(assessment)
    setActiveStep(1)
    setInputContent('')
    setAssessmentResult(null)
    setStreamingAssessment('')
  }

  const handleStreamingAssessment = async (data) => {
    if (data.type === 'chunk') {
      setStreamingAssessment(prev => prev + data.content)
    } else if (data.type === 'complete') {
      const completeAssessment = {
        original: inputContent,
        assessment: streamingAssessment,
        timestamp: new Date().toISOString()
      }
      setAssessmentResult(completeAssessment)
      setIsStreaming(false)
      setActiveStep(2)
      toast.success('Đánh giá hoàn tất!')
    } else if (data.type === 'error') {
      console.error('Streaming assessment error:', data.error)
      toast.error('Có lỗi xảy ra khi đánh giá')
      setIsStreaming(false)
    }
  }

  const handleSubmitAssessment = async () => {
    if (!inputContent.trim()) {
      toast.error('Vui lòng nhập nội dung cần đánh giá')
      return
    }

    setIsLoading(true)
    setIsStreaming(true)
    setStreamingAssessment('')
    setAssessmentResult(null)

    try {
      let requestData = {}
      
      switch (selectedAssessment.type) {
        case 'IELTS Writing Task 1':
          requestData = { content: inputContent, studentLevel }
          break
        case 'IELTS Writing Task 2':
          requestData = { content: inputContent, studentLevel }
          break
        case 'Speaking Assessment':
          requestData = { content: inputContent, studentLevel }
          break
        case 'General Level':
          requestData = { text: inputContent }
          break
        default:
          requestData = { content: inputContent, studentLevel }
      }

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api${selectedAssessment.streamEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              await handleStreamingAssessment(data)
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (error) {
      console.error('Assessment error:', error)
      toast.error('Có lỗi xảy ra khi đánh giá')
      setIsStreaming(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    setActiveStep(0)
    setSelectedAssessment(null)
    setInputContent('')
    setAssessmentResult(null)
    setStreamingAssessment('')
    setIsStreaming(false)
  }

  const getInputPlaceholder = () => {
    switch (selectedAssessment?.type) {
      case 'IELTS Writing Task 1':
        return 'Nhập bài viết Task 1 của bạn (mô tả biểu đồ, bảng, sơ đồ)...'
      case 'IELTS Writing Task 2':
        return 'Nhập bài luận Task 2 của bạn...'
      case 'Speaking Assessment':
        return 'Nhập transcript (bản ghi chép) của bài nói của bạn...'
      case 'General Level':
        return 'Nhập một đoạn văn tiếng Anh để đánh giá trình độ...'
      default:
        return 'Nhập nội dung cần đánh giá...'
    }
  }

  const wordCount = inputContent.trim().split(/\s+/).filter(word => word.length > 0).length

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <AssessmentIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
            Đánh giá
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Đánh giá kỹ năng tiếng Anh của bạn với sự hỗ trợ của AI
          </Typography>
        </Box>

        {/* Stepper */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Step Content */}
        <Paper elevation={3} sx={{ p: 4, minHeight: '60vh' }}>
          {activeStep === 0 && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
                Chọn loại đánh giá
              </Typography>
              <Grid container spacing={3}>
                {assessmentTypes.map((assessment, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        height: '100%',
                        '&:hover': { 
                          transform: 'translateY(-4px)',
                          boxShadow: 4
                        },
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => handleAssessmentSelect(assessment)}
                    >
                      <CardContent sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="h2" sx={{ mb: 2 }}>
                          {assessment.icon}
                        </Typography>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          {assessment.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {assessment.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          )}

          {activeStep === 1 && selectedAssessment && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  {selectedAssessment.title}
                </Typography>
                <Chip label={selectedAssessment.type} color="primary" sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  {selectedAssessment.description}
                </Typography>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={15}
                placeholder={getInputPlaceholder()}
                value={inputContent}
                onChange={(e) => setInputContent(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Số từ: {wordCount}
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    sx={{ mr: 2 }}
                  >
                    Quay lại
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmitAssessment}
                    disabled={isLoading || isStreaming || !inputContent.trim()}
                    startIcon={<CheckIcon />}
                  >
                    {isStreaming ? '🔄 Đang phân tích...' : isLoading ? 'Đang đánh giá...' : 'Bắt đầu đánh giá'}
                  </Button>
                </Box>
              </Box>

              {isLoading && <LinearProgress />}
              
              {/* Streaming Assessment Display */}
              {isStreaming && streamingAssessment && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert severity="info" sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>🔄 Đang phân tích đánh giá...</Typography>
                    <Box sx={{ 
                      maxHeight: '400px', 
                      overflow: 'auto',
                      '& p': { margin: '8px 0' },
                      '& ul, & ol': { paddingLeft: '20px' },
                      '& li': { margin: '4px 0' },
                      '& h1, & h2, & h3': { margin: '12px 0 8px 0' },
                      '& strong': { fontWeight: 600 }
                    }}>
                      <ReactMarkdown>{streamingAssessment}</ReactMarkdown>
                    </Box>
                  </Alert>
                </motion.div>
              )}
            </motion.div>
          )}

          {activeStep === 2 && assessmentResult && !isStreaming && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <SchoolIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Kết quả đánh giá
                </Typography>
                <Chip 
                  label={selectedAssessment.title} 
                  color="primary" 
                  size="large"
                />
              </Box>

              <Alert severity="success" sx={{ mb: 3 }}>
                Đánh giá hoàn tất! Xem chi tiết phản hồi bên dưới.
              </Alert>

              {assessmentResult.original && (
                <Paper elevation={1} sx={{ p: 3, bgcolor: 'info.50', mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    📝 Nội dung gốc
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    {assessmentResult.original}
                  </Typography>
                </Paper>
              )}

              <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50', mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <TrendingIcon sx={{ mr: 1, color: 'primary.main' }} />
                  Phản hồi chi tiết
                </Typography>
                <Box sx={{ 
                  '& p': { margin: '8px 0' },
                  '& ul, & ol': { paddingLeft: '20px' },
                  '& li': { margin: '4px 0' },
                  '& h1, & h2, & h3': { margin: '12px 0 8px 0' },
                  '& strong': { fontWeight: 600 }
                }}>
                  <ReactMarkdown>
                    {assessmentResult.feedback || assessmentResult.response || assessmentResult.assessment}
                  </ReactMarkdown>
                </Box>
                {assessmentResult.timestamp && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
                    Đánh giá lúc: {new Date(assessmentResult.timestamp).toLocaleString('vi-VN')}
                  </Typography>
                )}
              </Paper>

              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ mr: 2 }}
                >
                  Đánh giá mới
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(1)}
                >
                  Thử lại với nội dung khác
                </Button>
              </Box>
            </motion.div>
          )}
        </Paper>

        {/* Tips */}
        <Paper elevation={2} sx={{ p: 3, mt: 4, bgcolor: 'warning.50' }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'warning.main' }}>
            💡 Lưu ý khi sử dụng tính năng đánh giá
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1 }}>• Viết đầy đủ và rõ ràng để có đánh giá chính xác</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>• Đối với IELTS, tuân thủ số từ yêu cầu</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1 }}>• Sử dụng kết quả để cải thiện kỹ năng</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>• Thực hành thường xuyên để nâng cao trình độ</Typography>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>
    </Container>
  )
}

export default Assessment